import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import { getImage } from '@aytm/app/stats/lib/stat-page'
import { isWCType as calcIsWCType, processText } from '@aytm/app/stats/page/components/lib/utils'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { calcControlsProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/controls'
import { calcWordCloudProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/word-cloud'
import { calcChartProps } from '@aytm/app/stats/page/question-views/mx/components/items/lib/props/chart'
import { calcTableProps } from '@aytm/app/stats/page/question-views/mx/components/items/lib/table-utils'
import { calcChartPlaceholderHeight } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/placeholder'

import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

import useSubItemMatrixState from '@aytm/app/stats/page/question-views/mx/components/items/states/useSubItemMatrixState'

// components
import ImagePopup from '@aytm/app/stats/components/image-popup/ImagePopup'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import Chart from '@aytm/app/stats/page/question-views/answer-variants/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import ItemsControls from '@aytm/app/stats/page/question-views/components/controls/Controls'
import Items from '@aytm/app/stats/page/question-views/oex/components/Items'
import { BlockContent as OeBlock } from '@aytm/app/stats/page/question-views/oe/Block'

import style from '@aytm/app/stats/page/question-views/mx/components/items/styles/Items.module.scss'

// A ranking question numbers its rows as answers; every other matrix flavour numbers them as
// sub-questions.
const ANSWER_NUMBERED_TYPES = ['ro', 'aro', 'sft']

const prefixOf = questionType => (ANSWER_NUMBERED_TYPES.includes(questionType) ? 'A' : 'SQ')

// An item can be expanded unless the question says its items do not open.
export const isCollapsable = components => undefined === components?.expand || Boolean(components.expand)

// The row an item shows while it is closed: what it asks, its image, and the expander. A hidden
// item has no row of its own -- it is the only one there is, so it is shown open without a header.
// Whether the rows open at all is the list's to decide, so it is asked rather than re-derived here.
export const ItemHeader = ({ item, questionType, collapsed, expandable }) => {
  if (item.hidden) return null

  const image = getImage(item.image)

  return (
    <div className={style.description}>
      {image?.original ? (
        // the popup draws the thumbnail itself and opens on hover; the row toggles on click, so the
        // click is stopped here to keep inspecting an image from collapsing the item
        <div className={style.thumbnail} onClick={event => event.stopPropagation()}>
          <ImagePopup image={image} width={24} height={24} />
        </div>
      ) : null}

      <span className={style.title}>
        {`${prefixOf(questionType)}${item.position}: `}
        {/* processText marks up logic, piping and aliases, so the title carries its html */}
        <span dangerouslySetInnerHTML={{ __html: processText(item.text) }} />
      </span>

      {expandable ? (
        <span className={classNames(style.expand, { [style.expandOpen]: !collapsed })}>
          <i className="icon-aytmfd-chevron-bracket" />
        </span>
      ) : null}
    </div>
  )
}

// One item of a matrix-family question, drawn as a question of its own: a strip of controls, then
// either a chart over the item's answer variants or the word cloud they make between them, then
// those variants as a table.
//
// Replaces subitems/matrix/block.coffee and the chart and items-list views under it. The list that
// owns this item's report params resolves them and hands them over; everything the researcher sees
// and moves is here, and the item answers the exporters through the api it hands up.
export const ItemBody = props => {
  const {
    id,
    questionKey,
    questionType,
    questionSubType,
    format,
    questionChartMode,
    components,
    answer,
    answerVariants,
    params,
    defaultSortOrder,
    // the per-variant open-ended list, for a question configured to show one; the shell resolves
    // each variant's block, so an item it has built none for draws nothing
    variantsUncollapsed,
    oePropsOf,
    handlers = {},
    onReady,
    onStateChange,
    onWcParamsChange,
    onVariantsToggle
  } = props

  const {
    chartMode,
    zoomLevel,
    sortOrder,
    modelChartMode,
    isWordCloud,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onWcReady,
    onWcToggle
  } = useSubItemMatrixState({
    params,
    paramsRevision: props.paramsRevision,
    questionKey,
    questionType,
    questionSubType,
    format,
    questionChartMode,
    answer,
    answerVariants,
    defaultSortOrder,
    onReady,
    onStateChange
  })

  const question = { ...props, chartMode, zoomLevel, sortOrder }

  const cloudHandlers = useMemo(() => ({ ...handlers, onWcReady }), [handlers, onWcReady])

  const chartHandlers = useMemo(
    () => ({ ...handlers, onChartModeChange, onZoomChange, onChartReady }),
    [handlers, onChartModeChange, onZoomChange, onChartReady]
  )

  // a clicked column names the order it wants; the state is what settles on one
  const onTableSort = useMemo(
    () => (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) onSortChange(order)
    },
    [onSortChange]
  )

  const chartProps = calcChartProps({ ...question, modelChartMode, handlers: chartHandlers })

  // how tall the frame is held open until the item's chart is scrolled to; a mode that draws no
  // chart at all answers null and falls back to the family height
  const chartHeight = calcChartPlaceholderHeight({
    ...chartProps,
    itemCount: chartProps.answerVariants?.length || 0
  })

  // a word cloud is only offered to an item carrying enough answer variants of the right kind
  const isWCType = calcIsWCType({ questionType, batches: null, answerVariants })

  // a report, or a question, drawn as tables only has no chart to offer here either -- a
  // sub-question is a question, and reads the setting the same way its parent does
  const chartOn = !isNoneChartMode(questionChartMode, chartProps.globalChartMode)

  return (
    <>
      <ItemsControls
        {...calcControlsProps({ ...question, handlers: { ...handlers, onChartModeChange: onWcToggle } })}
      />

      {isWCType && isWordCloud ? (
        <OeBlock {...calcWordCloudProps({ ...question, handlers: cloudHandlers, onWcParamsChange })} />
      ) : (
        isComponentOn(components, 'chart') &&
        chartOn && (
          <ChartPlaceholder height={chartHeight} isLoading={'loading' === answer}>
            <Chart {...chartProps} />
          </ChartPlaceholder>
        )
      )}

      {isComponentOn(components, 'table') && (
        <Table
          {...calcTableProps({
            ...question,
            questionId: id,
            // with no chart above it the list is the only place the magnitudes can be read
            bars: !chartOn,
            handlers: { ...handlers, onSort: onTableSort }
          })}
        />
      )}

      {/* an item shows its variants' verbatims only where the question asks for them, which is why
          this is a plain check and not the undefined-is-on one the other surfaces use */}
      {components?.oe_answers && isComponentOn(components, 'fields') ? (
        <Items
          questionId={id}
          answerVariants={answerVariants}
          type={questionType}
          answerLabels={format?.answerLabels}
          uncollapsed={variantsUncollapsed}
          oePropsOf={oePropsOf}
          onToggle={onVariantsToggle}
          onToggleAll={onVariantsToggle}
        />
      ) : null}
    </>
  )
}

ItemHeader.displayName = 'MatrixItemHeader'
ItemBody.displayName = 'MatrixItemBody'

ItemHeader.propTypes = {
  item: PropTypes.object.isRequired,
  questionType: PropTypes.string,
  collapsed: PropTypes.bool,
  expandable: PropTypes.bool
}

ItemBody.propTypes = {
  id: PropTypes.string,
  questionKey: PropTypes.string,
  questionType: PropTypes.string,
  questionSubType: PropTypes.string,
  format: PropTypes.object,
  // the chart mode the question this batch hangs on declares in its data
  questionChartMode: PropTypes.string,
  components: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  item: PropTypes.object,
  params: PropTypes.object,
  paramsRevision: PropTypes.number,
  defaultSortOrder: PropTypes.string,
  variantsUncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  oePropsOf: PropTypes.func,
  handlers: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onWcParamsChange: PropTypes.func,
  onVariantsToggle: PropTypes.func
}
